import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";

import "react-quill-new/dist/quill.snow.css";
import "./RichTextEditor.scss";

// 글자 크기를 px 단위로 세밀하게 지정할 수 있도록 화이트리스트 등록 (모듈 로드 시 1회)
const SizeStyle = Quill.import("attributors/style/size") as { whitelist: string[] };
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "20px", "24px", "32px"];
Quill.register(SizeStyle as never, true);

const SIZE_OPTIONS = ["12px", "14px", false, "18px", "20px", "24px", "32px"];

// 글꼴(한글 웹폰트 포함) 화이트리스트 등록. 클래스 기반(ql-font-*)이며,
// 실제 font-family 매핑과 드롭다운 라벨은 RichTextEditor.scss 에서 처리한다.
const FONT_OPTIONS = ["noto-sans-kr", "nanum-gothic", "nanum-myeongjo", "gowun-dodum", "gaegu", "serif", "monospace"];
const Font = Quill.import("formats/font") as { whitelist: string[] };
Font.whitelist = FONT_OPTIONS;
Quill.register(Font as never, true);

/** ReactQuill 이 알려주는 변경 출처. `api` 는 Quill 자체 정규화, `user` 는 실제 사용자 편집이다. */
export type RichTextChangeSource = "user" | "api" | "silent";

export interface RichTextEditorProps {
  value: string;
  /**
   * 본문이 바뀔 때 호출된다.
   * `source` 는 **선택적**이라 첫 인자만 쓰는 기존 소비자는 그대로 동작한다.
   * 사용자가 실제로 편집했는지 구분해야 하는 화면만 `source === "user"` 를 보면 된다.
   */
  onChange: (html: string, source?: RichTextChangeSource) => void;
  /** 이미지 파일을 서버에 업로드하고 접근 가능한 URL을 반환하는 함수 */
  uploadImage: (file: File) => Promise<string>;
  placeholder?: string;
  height?: number;
}

/**
 * Quill 기반 WYSIWYG 에디터.
 * - 글자 크기/색상/밑줄/두께 등 서식 지원
 * - 이미지 첨부 시 서버 업로드 후 반환된 URL을 본문에 삽입
 * - 드래그 앤 드롭 및 클립보드 붙여넣기로 이미지 업로드
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, uploadImage, placeholder, height = 420 }) => {
  const quillRef = useRef<ReactQuill>(null);
  const [uploading, setUploading] = useState(false);

  // 부모가 렌더마다 새 함수를 넘겨도 modules/effect 의존성이 바뀌지 않도록 ref로 고정.
  // (의존성이 바뀌면 ReactQuill이 에디터를 파괴·재생성하며 "non-instantiated editor" 에러 발생)
  const uploadImageRef = useRef(uploadImage);
  useEffect(() => {
    uploadImageRef.current = uploadImage;
  }, [uploadImage]);

  // ReactQuill 은 (html, delta, source, editor) 로 호출한다. 소비자에게는 Delta·editor 를
  // 노출하지 않고 (html, source) 로만 좁혀 전달한다 — 그대로 흘리면 두 번째 인자가 Delta 가 되어
  // 사용자 편집과 Quill 정규화를 구분할 수 없다.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleChange = useCallback((html: string, _delta: unknown, source: RichTextChangeSource) => {
    onChangeRef.current(html, source);
  }, []);

  // 던지는 getEditor() 대신 공개 필드를 직접 읽어 인스턴스 미생성 시 undefined 반환
  const getQuill = useCallback(() => quillRef.current?.editor ?? null, []);

  // 파일 1개를 업로드하고 현재 커서 위치에 삽입
  const insertImageFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) return;
      const editor = getQuill();
      if (!editor) return;

      setUploading(true);
      try {
        const url = await uploadImageRef.current(file);
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertEmbed(index, "image", url, "user");
        editor.setSelection(index + 1, 0);
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert("이미지 업로드에 실패했습니다. 다시 시도해주세요.");
      } finally {
        setUploading(false);
      }
    },
    [getQuill],
  );

  // 툴바 이미지 버튼: 파일 선택 → 업로드 → 삽입
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void insertImageFile(file);
    };
    input.click();
  }, [insertImageFile]);

  // modules는 한 번만 생성되어 에디터가 재생성되지 않도록 고정. imageHandler는 ref로 호출.
  const imageHandlerRef = useRef(imageHandler);
  useEffect(() => {
    imageHandlerRef.current = imageHandler;
  }, [imageHandler]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [[{ font: FONT_OPTIONS }], [{ size: SIZE_OPTIONS }], [{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"], [{ color: [] }, { background: [] }], [{ list: "ordered" }, { list: "bullet" }], [{ align: [] }], ["link", "image"], ["clean"]],
        handlers: { image: () => imageHandlerRef.current() },
      },
      clipboard: { matchVisual: false },
    }),
    [],
  );

  const formats = useMemo(() => ["font", "size", "header", "bold", "italic", "underline", "strike", "color", "background", "list", "align", "link", "image"], []);

  // 드래그 앤 드롭 / 붙여넣기로 이미지 업로드 (마운트 시 1회 등록)
  useEffect(() => {
    const root = getQuill()?.root;
    if (!root) return;

    const handleDrop = (e: DragEvent) => {
      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith("image/"));
      if (files.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      files.forEach((file) => void insertImageFile(file));
    };

    const handlePaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith("image/"));
      if (files.length === 0) return;
      e.preventDefault();
      files.forEach((file) => void insertImageFile(file));
    };

    root.addEventListener("drop", handleDrop, true);
    root.addEventListener("paste", handlePaste, true);
    return () => {
      root.removeEventListener("drop", handleDrop, true);
      root.removeEventListener("paste", handlePaste, true);
    };
  }, [getQuill, insertImageFile]);

  return (
    <div className="rich-text-editor" style={{ height }}>
      <ReactQuill ref={quillRef} theme="snow" value={value} onChange={handleChange} modules={modules} formats={formats} placeholder={placeholder} />
      {uploading && <div className="rich-text-editor__uploading">이미지 업로드 중…</div>}
    </div>
  );
};

export default RichTextEditor;
